import { useContext, useMemo } from 'react';
import type {
  CustomObjectLike,
  FieldLike,
  GenericOption,
  MappingRule,
} from 'pages/SmartConnectors/types';
import { SMART_FIELD_TYPES } from 'pages/SmartConnectors/constants';
import { PrimaryObjectUploadContext } from '../../context';
import { v3 as uuid3 } from 'uuid';

export type UseCustomObjectFieldsResult = {
  fieldsById: Record<string, FieldLike>;
  fieldsByCategory: Record<string, FieldLike[]>;
  fieldForCreate: FieldLike | undefined;
  fieldOptions: { label: string; options: GenericOption[] }[];
  categoryOptions: GenericOption[];
};

export type RulesByFieldId = Record<
  string,
  { rule: MappingRule; index: number }
>;

export const useCustomObjectFields = (
  customObject: CustomObjectLike,
  rulesByFieldId: RulesByFieldId
) => {
  const { metaData } = useContext(PrimaryObjectUploadContext);

  const {
    fieldsById,
    fieldsByCategory,
    fieldForCreate,
    categoryOptions,
    systemCategory,
  } = useMemo<
    Omit<UseCustomObjectFieldsResult, 'fieldOptions'> & {
      systemCategory?: {
        id: string;
        name: string;
        order: number;
        options: FieldLike[];
      };
    }
  >(() => {
    const systemCategoryId =
      customObject?.id &&
      uuid3('sys:category:other_system_fields', customObject.id);
    const systemCategory =
      metaData.field_mapping_rules.other_system_fields && customObject?.id
        ? {
            id: systemCategoryId,
            name: metaData.field_mapping_rules.other_system_fields.display_name,
            order: Infinity,
            options:
              metaData.field_mapping_rules.other_system_fields.fields.map(
                (field) => ({
                  id: uuid3(`sys:field:${field.field_type}`, customObject.id),
                  category: systemCategoryId,
                  name: field.name,
                  displayName: field.display_name,
                  fieldType: field.field_type,
                  properties: {
                    allowMultiple: field.properties.allow_multiple,
                  },
                  isReadOnly: false,
                })
              ),
          }
        : undefined;

    const fields = [
      ...(customObject?.fields || []),
      ...(systemCategory ? systemCategory.options : []),
    ];

    const fieldsById = fields.reduce(
      (acc, field) => ({ ...acc, [field.id]: field }),
      {} as Record<string, FieldLike>
    );

    const fieldForCreate = fields.find(
      ({ name }) => name === 'name' || name === 'email'
    );

    const fieldsByCategory = fields.reduce(
      (acc, field) => {
        if (
          field.id === fieldForCreate?.id ||
          !SMART_FIELD_TYPES[field.fieldType] ||
          field.isReadOnly
        ) {
          return acc;
        }
        if (acc[field.category]) {
          acc[field.category].push(field);
        } else {
          acc[field.category] = [field];
        }
        return acc;
      },
      {} as Record<string, FieldLike[]>
    );

    const categoryOptions = [
      ...(customObject?.fieldCategories || []),
      ...(systemCategory ? [systemCategory] : []),
    ]
      .sort(({ order: a }, { order: b }) => a - b)
      .map(({ id, name }) => ({
        value: id,
        label: name,
      }));

    return {
      fieldsById,
      fieldsByCategory,
      fieldForCreate,
      categoryOptions,
      systemCategory,
    };
  }, [
    customObject?.fields,
    customObject?.fieldCategories,
    metaData,
    customObject?.id,
  ]);

  const categorizedFieldOptions = useMemo(() => {
    return [
      ...(customObject?.fieldCategories || []),
      ...(systemCategory ? [systemCategory] : []),
    ]
      .sort(({ order: a }, { order: b }) => a - b)
      .reduce<
        {
          label: string;
          options: (GenericOption & { field: FieldLike })[];
        }[]
      >((acc, { id, name }) => {
        const options = (fieldsByCategory[id] || []).reduce<
          (GenericOption & { field: FieldLike })[]
        >((acc, field) => {
          const { id: fieldId, displayName, name } = field;
          if (!rulesByFieldId[fieldId]) {
            const option = {
              value: fieldId,
              label: `${displayName} (${name})`,
              field,
            };
            acc.push(option);
          }
          return acc;
        }, []);

        if (options.length) {
          acc.push({ label: name, options });
        }

        return acc;
      }, []);
  }, [
    customObject?.fieldCategories,
    fieldsByCategory,
    rulesByFieldId,
    systemCategory,
  ]);

  return {
    fieldsById,
    fieldsByCategory,
    fieldForCreate,
    categorizedFieldOptions,
    categoryOptions,
  };
};
